import { SharedModule } from '@@shared/shared.module'
import { CommonModule } from '@angular/common'
import { Component, OnInit, Input, ViewChild } from '@angular/core'
import { MatMenuTrigger } from '@angular/material/menu'
import { TranslateService } from '@ngx-translate/core'
import { SupportedLanguage } from '@@interfaces/language.model'
import { LanguageService } from '@@services/language.service'
import { LOCAL_STORAG_KEYS } from '@@constants/local-storage-keys'

@Component({
    standalone: true,
    imports: [SharedModule, CommonModule],
    selector: 'language-selector',
    templateUrl: './language-selector.component.html',
    styleUrl: './language-selector.component.scss'
})
export class LanguageSelectorComponent implements OnInit {
    availableLanguages: SupportedLanguage[] = []
    selectedLanguage!: string
    isDisabled: boolean = false
    isLoading: boolean = true

    @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger

    @Input() forceEnabled: boolean = false
    @Input() useGlobeIcon: boolean = false
    @Input() showLabel: boolean = false

    constructor(
        private translate: TranslateService,
        private languageService: LanguageService
    ) {
        this.selectedLanguage = this.translate.currentLang
    }

    ngOnInit() {
        this.fetchTenantLanguages()
    }

    fetchTenantLanguages() {
        // If forceEnabled is true (e.g., on login page), show all languages regardless of tenant
        if (this.forceEnabled) {
            this.showAllSupportedLanguages()
            return
        }

        // Check if tenant ID exists before fetching languages
        const tenantId = localStorage.getItem(LOCAL_STORAG_KEYS.TENANT_ID)


        if (!tenantId || tenantId === 'null' || tenantId === 'undefined' || tenantId.trim() === '') {
            // No tenant selected yet (e.g., on company selection page)
            // Show only current language
            this.availableLanguages = [{
                name: this.selectedLanguage.toUpperCase(),
                subName: this.selectedLanguage.toUpperCase(),
                code: this.selectedLanguage,
                flagCode: this.getFlagCodeFromLocale(this.selectedLanguage)
            }]
            this.isDisabled = true
            this.isLoading = false
            return
        }

        this.isLoading = true
        this.languageService.getSupportedLanguages().subscribe({
            next: (response) => {
                this.availableLanguages = response.languages || []
                this.isDisabled = this.availableLanguages.length <= 1
                this.isLoading = false
            },
            error: (error) => {
                // On error, show only current language
                this.availableLanguages = [{
                    name: this.selectedLanguage.toUpperCase(),
                    subName: this.selectedLanguage.toUpperCase(),
                    code: this.selectedLanguage,
                    flagCode: this.getFlagCodeFromLocale(this.selectedLanguage)
                }]
                this.isDisabled = true
                this.isLoading = false
            }
        })
    }

    showAllSupportedLanguages() {
        // Show all supported languages when forceEnabled is true
        this.availableLanguages = [
            { name: 'ENGLISH', subName: 'ENGLISH', code: 'en', flagCode: 'us' },
            { name: 'ARABIC', subName: 'ARABIC', code: 'ar', flagCode: 'sa' },
            { name: 'FRENCH', subName: 'FRENCH', code: 'fr', flagCode: 'fr' },
            { name: 'HINDI', subName: 'HINDI', code: 'hi', flagCode: 'in' },
            { name: 'FILIPINO', subName: 'FILIPINO', code: 'tl', flagCode: 'ph' },
            { name: 'URDU', subName: 'URDU', code: 'ur', flagCode: 'pk' }
        ]
        this.isDisabled = false
        this.isLoading = false
    }

    selectLanguage(lang: string) {
        if (this.isDisabled) return

        const previousLanguage = this.selectedLanguage

        // If forceEnabled (e.g., on login page), skip backend call and just switch language locally
        if (this.forceEnabled) {
            this.selectedLanguage = lang
            this.languageService.setLanguage(lang)
            return
        }

        // Update user language in backend first
        this.languageService.updateUserLanguage(lang).subscribe({
            next: () => {
                // Only switch language if backend update succeeds
                this.selectedLanguage = lang
                
                // Update localStorage immediately before reload
                localStorage.setItem(LOCAL_STORAG_KEYS.LANGUAGE_SELECTED, lang)
                const userProfileStr = localStorage.getItem(LOCAL_STORAG_KEYS.USER_PROFILE)
                if (userProfileStr) {
                    try {
                        const profile = JSON.parse(userProfileStr)
                        if (profile.language && typeof profile.language === 'object') {
                            profile.language.locale = lang
                        } else if (profile.language) {
                            profile.language = lang
                        }
                        profile.defaultLanguage = lang
                        localStorage.setItem(LOCAL_STORAG_KEYS.USER_PROFILE, JSON.stringify(profile))
                    } catch (e) {
                        console.error('Error updating user profile language in localStorage:', e)
                    }
                }
                
                // Set language in translate service
                this.languageService.setLanguage(lang)
                
                // Refresh page after language change when logged in
                window.location.reload()
            },
            error: (error) => {
                // Revert to previous language on error
                this.selectedLanguage = previousLanguage
            }
        })
    }

    openMenu() {
        if (!this.isDisabled && !this.isLoading && this.menuTrigger) {
            this.menuTrigger.openMenu()
        }
    }

    getFlagCodeFromLocale(locale: string): string {
        const flagMap: { [key: string]: string } = {
            'en': 'us',
            'ar': 'sa',
            'fr': 'fr',
            'hi': 'in',
            'tl': 'ph',
            'ur': 'pk'
        }
        const result = flagMap[locale] || 'us'
        return result
    }
}
